import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Download, Save, Upload } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

// The book settings KF admin may change. An empty field uses the default.
function BookSettings({ settings }) {
  const [form, setForm] = useState({ paper_caliper: settings.paperCaliper, qr_url: settings.qrUrl, flag_email: settings.flagEmail })
  const field = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) })

  return (
    <Card>
      <CardHeader><CardTitle className="text-lg">Book settings</CardTitle></CardHeader>
      <CardContent>
        <form className="space-y-4 text-sm" onSubmit={(e) => { e.preventDefault(); router.patch("/app/admin/book_settings", form, { preserveScroll: true }) }}>
          <p className="text-zinc-600">A change to the paper thickness or the QR address changes every cover. Each book not yet sent to print then needs a new assembly. Leave a field empty to use the default.</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="paper_caliper">Paper thickness per page (inches)</Label>
              <Input id="paper_caliper" inputMode="decimal" placeholder={settings.paperCaliperDefault} {...field("paper_caliper")} />
              <p className="text-xs text-zinc-500">From the printer. Sets the spine width. Default {settings.paperCaliperDefault}.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qr_url">QR code address</Label>
              <Input id="qr_url" type="url" placeholder={settings.qrUrlDefault} {...field("qr_url")} />
              <p className="text-xs text-zinc-500">The back cover's QR code opens this page. Default {settings.qrUrlDefault}.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="flag_email">Flag emails go to</Label>
              <Input id="flag_email" placeholder="Every admin" {...field("flag_email")} />
              <p className="text-xs text-zinc-500">Who gets the serious Brand check flags and Send to print emails. Put a comma between two addresses. What the check flags is on <Link href="/app/admin/brand_check_rules" className="underline">Brand check rules</Link>.</p>
            </div>
          </div>
          <Button type="submit"><Save className="h-4 w-4" /> Save the settings</Button>
        </form>
      </CardContent>
    </Card>
  )
}

// What every Kingdom Factors book shares: the KF back matter PDF and the
// book settings. The cover image library has its own page.
export default function BookFilesIndex({ currentUser, backMatter, settings }) {
  return (
    <AdminLayout currentUser={currentUser}>
      <Head title="Book files - Kingdom Factor" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Book files</h1>
          <p className="text-zinc-600">The files every Kingdom Factors book shares. A change here goes into each book's next assembly.</p>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-lg">KF back matter</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-zinc-600">
              The Bonus content pages after the chapters, the same in every book. Now: {backMatter.pages} pages,{" "}
              {backMatter.uploadedAt ? `uploaded ${new Date(backMatter.uploadedAt).toLocaleDateString()} by ${backMatter.uploadedBy}` : "made from Ray's draft 36"}.
            </p>
            {backMatter.url && <Button asChild size="sm" variant="outline"><a href={backMatter.url}><Download className="h-4 w-4" /> Download the current PDF</a></Button>}
            <form className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault()
                const pdf = e.currentTarget.elements.pdf.files?.[0]
                if (pdf) router.post("/app/admin/book_back_matters", { book_back_matter: { pdf } }, { forceFormData: true })
              }}>
              <div className="space-y-1.5">
                <Label htmlFor="pdf">New back matter (PDF, every page 5.5 × 8.5 in)</Label>
                <Input id="pdf" name="pdf" type="file" accept="application/pdf" required />
              </div>
              <Button type="submit"><Upload className="h-4 w-4" /> Replace the back matter</Button>
            </form>
          </CardContent>
        </Card>

        <BookSettings settings={settings} />
      </div>
    </AdminLayout>
  )
}
